import * as React from 'react';
import { Avatar, Box, Paper, Stack, Typography } from '@mui/material';
import { styled, alpha } from '@mui/material/styles';
import { SmartToyOutlined } from '@mui/icons-material';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';

export interface DisplayMessage {
    id: number;
    role: 'user' | 'assistant';
    text: string;
    isStreaming?: boolean;
}

interface AIChatMessageProps {
    message: DisplayMessage;
    userInitial: string;
}

// Markdown elements rendered inside an assistant bubble. Tight spacing because the bubble is
// narrow; headers are scaled down so they don't dwarf surrounding chat text.
const MarkdownContainer = styled(Box)(({ theme }) => ({
    fontSize: '0.875rem',
    lineHeight: 1.55,
    color: theme.vars.palette.text.primary,
    wordBreak: 'break-word',

    '& > :first-of-type': { marginTop: 0 },
    '& > :last-child': { marginBottom: 0 },

    '& p': {
        margin: theme.spacing(0.5, 0),
    },

    '& h1, & h2, & h3, & h4, & h5, & h6': {
        margin: theme.spacing(1, 0, 0.5),
        fontWeight: 700,
        lineHeight: 1.3,
    },
    '& h1': { fontSize: '1.05rem' },
    '& h2': { fontSize: '1rem' },
    '& h3': { fontSize: '0.95rem' },
    '& h4, & h5, & h6': { fontSize: '0.9rem' },

    '& ul, & ol': {
        margin: theme.spacing(0.5, 0),
        paddingInlineStart: theme.spacing(2.5),
    },
    '& li': {
        margin: theme.spacing(0.25, 0),
    },
    '& li > p': {
        margin: 0,
    },

    '& strong': { fontWeight: 700 },
    '& em': { fontStyle: 'italic' },

    '& a': {
        color: theme.vars.palette.primary.main,
        textDecoration: 'underline',
        textUnderlineOffset: 2,
    },

    '& blockquote': {
        margin: theme.spacing(0.75, 0),
        padding: theme.spacing(0.5, 1.25),
        borderInlineStart: `3px solid ${alpha(theme.palette.primary.main, 0.5)}`,
        backgroundColor: alpha(theme.palette.primary.main, 0.04),
        borderRadius: theme.spacing(0.5),
        color: theme.vars.palette.text.secondary,
        '& > p': { margin: 0 },
    },

    '& code': {
        fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace',
        fontSize: '0.82em',
        padding: theme.spacing(0.15, 0.5),
        borderRadius: theme.spacing(0.5),
        backgroundColor: alpha(theme.palette.text.primary, 0.08),
    },
    '& pre': {
        margin: theme.spacing(0.75, 0),
        padding: theme.spacing(1),
        borderRadius: theme.spacing(1),
        overflowX: 'auto',
        backgroundColor: alpha(theme.palette.text.primary, 0.06),
        fontSize: '0.8rem',
    },
    '& pre code': {
        background: 'transparent',
        padding: 0,
    },

    '& hr': {
        border: 'none',
        height: 1,
        backgroundColor: alpha(theme.palette.divider, 0.6),
        margin: theme.spacing(1, 0),
    },

    '& table': {
        borderCollapse: 'collapse',
        width: '100%',
        margin: theme.spacing(0.75, 0),
        fontSize: '0.82rem',
    },
    '& th, & td': {
        border: `1px solid ${alpha(theme.palette.divider, 0.6)}`,
        padding: theme.spacing(0.5, 0.75),
        textAlign: 'start',
    },
    '& th': {
        backgroundColor: alpha(theme.palette.primary.main, 0.06),
        fontWeight: 600,
    },
}));

const AIChatMessage: React.FC<AIChatMessageProps> = ({ message, userInitial }) => {
    const isUser = message.role === 'user';

    return (
        <Stack
            direction={isUser ? 'row-reverse' : 'row'}
            spacing={1}
            sx={{ mb: 1.5, alignItems: 'flex-start' }}
        >
            <Avatar
                sx={(theme) => ({
                    width: 30,
                    height: 30,
                    fontSize: 14,
                    bgcolor: isUser ? 'primary.main' : alpha(theme.palette.secondary.main, 0.85),
                })}
            >
                {isUser ? userInitial : <SmartToyOutlined sx={{ fontSize: 18 }} />}
            </Avatar>
            <Box sx={{ maxWidth: 'calc(100% - 50px)', minWidth: 0, flex: 1 }}>
                <Paper
                    elevation={0}
                    sx={(theme) => ({
                        p: 1.25,
                        borderRadius: 2,
                        // Drawer surface is already `background.paper`; if the assistant bubble
                        // also uses `background.paper`, the two merge in dark mode and the
                        // message visually disappears. Use a slightly elevated surface that
                        // adapts per mode so the bubble always stands off the drawer.
                        backgroundColor: isUser
                            ? alpha(theme.palette.primary.main, 0.08)
                            : alpha(theme.palette.text.primary, 0.04),
                        border: `1px solid ${alpha(theme.palette.divider, 0.6)}`,
                        ...theme.applyStyles('dark', {
                            backgroundColor: isUser
                                ? alpha(theme.palette.primary.main, 0.18)
                                : alpha(theme.palette.common.white, 0.06),
                        }),
                    })}
                >
                    {isUser ? (
                        message.text ? (
                            <Typography
                                variant="body2"
                                sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}
                            >
                                {message.text}
                            </Typography>
                        ) : null
                    ) : message.text ? (
                        <MarkdownContainer>
                            <ReactMarkdown remarkPlugins={[remarkGfm]}>
                                {message.text + (message.isStreaming ? '…' : '')}
                            </ReactMarkdown>
                        </MarkdownContainer>
                    ) : message.isStreaming ? (
                        <Typography variant="body2" color="text.secondary">
                            …
                        </Typography>
                    ) : null}
                </Paper>
            </Box>
        </Stack>
    );
};

export default AIChatMessage;
